import React, { useState, useEffect } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    IconButton,
    CircularProgress
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TableChartIcon from '@mui/icons-material/TableChart';
import SettingsIcon from '@mui/icons-material/Settings';
// Common icons for applications
import BusinessIcon from '@mui/icons-material/Business';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PeopleIcon from '@mui/icons-material/People';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CategoryIcon from '@mui/icons-material/Category';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
// Import our dynamic menu service
import { getDynamicMenuItems } from '../services/menuService';

// ============================================================================
// Types
// ============================================================================
interface MenuItemBase {
    text: string;
    icon: React.ReactElement;
}

interface MenuItemLink extends MenuItemBase {
    type: 'link';
    path: string;
}

interface MenuItemParent extends MenuItemBase {
    type: 'parent';
    children: MenuItemLink[];
    defaultOpen?: boolean;
}

type MenuItem = MenuItemLink | MenuItemParent;

// ============================================================================
// Constants
// ============================================================================
// Top menu items (Dashboard only)
const topMenuItems: MenuItem[] = [
    { type: 'link', text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
];

// Bottom menu items (System only)
const bottomMenuItems: MenuItem[] = [
    { type: 'link', text: 'System', icon: <SettingsIcon />, path: '/system' },
];

// Simple icon mapping - much cleaner than giant switch statement
const iconMap: Record<string, React.ComponentType> = {
  'settings': SettingsIcon,
  'business': BusinessIcon,
  'smart_toy': SmartToyIcon,
  'favorite': FavoriteIcon,
  'people': PeopleIcon,
  'card_giftcard': CardGiftcardIcon,
  'card_membership': CardMembershipIcon,
  'favorite_border': FavoriteBorderIcon,
  'category': CategoryIcon,
  'photo_camera': PhotoCameraIcon,
};

// ============================================================================
// Helper Functions
// ============================================================================
const getIconComponent = (iconName?: string): React.ReactElement => {
  const IconComponent = iconName ? iconMap[iconName.toLowerCase()] : undefined;
  const Icon = IconComponent || TableChartIcon;
  return <Icon />;
};

// ============================================================================
// Interfaces
// ============================================================================
interface SidebarProps {
    drawerOpen: boolean;
    toggleDrawer: () => void;
}

// ============================================================================
// Component
// ============================================================================
export default function Sidebar({ drawerOpen, toggleDrawer }: SidebarProps) {
    const location = useLocation();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [middleMenuItems, setMiddleMenuItems] = useState<MenuItem[]>([]);
    
    // Fetch dynamic menu items
    useEffect(() => {
        const fetchMenuData = async () => {
            setIsLoading(true);
            try {
                const { applications, tablesByApp } = await getDynamicMenuItems();
                
                // Build middle menu items (applications)
                const dynamicMenuItems: MenuItem[] = [];
                
                // Add applications as parent menu items
                applications.forEach(app => {
                    // Only show applications that are marked to show in sidebar
                    if (app.show_in_sidebar !== false && tablesByApp[app.id] && tablesByApp[app.id].length > 0) {
                        // Convert application to menu item with child tables
                        const appMenuItem: MenuItemParent = {
                            type: 'parent',
                            text: app.display_name || app.name,
                            icon: getIconComponent(app.icon),
                            defaultOpen: false,
                            children: tablesByApp[app.id].map(table => ({
                                type: 'link', 
                                text: table.label || table.table_name,
                                icon: getIconComponent(table.icon),
                                path: `/${table.table_name}`
                            }))
                        };
                        
                        // Add to menu items
                        dynamicMenuItems.push(appMenuItem);
                    }
                });
                
                setMiddleMenuItems(dynamicMenuItems);
            } catch (error) {
                console.error("Failed to load dynamic menu:", error);
                // Fallback to empty middle menu
                setMiddleMenuItems([]);
            } finally {
                setIsLoading(false);
            }
        };
        
        fetchMenuData();
    }, []);
    
    // Track which parent menus are open
    const [openMenus, setOpenMenus] = useState<Record<string, boolean>>(() => {
        // Initialize with default open states
        const defaultState: Record<string, boolean> = {};
        const allMenuItems = [...topMenuItems, ...middleMenuItems, ...bottomMenuItems];
        allMenuItems.forEach(item => {
            if (item.type === 'parent' && item.defaultOpen) {
                defaultState[item.text] = true;
            }
        });
        return defaultState;
    });

    // Update open menus when dynamic menu items are loaded
    useEffect(() => {
        if (!isLoading) {
            const updatedState = { ...openMenus };
            middleMenuItems.forEach(item => {
                if (item.type === 'parent' && item.defaultOpen) {
                    updatedState[item.text] = true;
                }
            });
            setOpenMenus(updatedState);
        }
    }, [isLoading, middleMenuItems]);

    const handleToggleMenu = (text: string) => {
        setOpenMenus(prevState => ({
            ...prevState,
            [text]: !prevState[text]
        }));
    };

    // Check if a menu item is selected based on current path
    const isSelected = (path: string) => {
        return location.pathname === path;
    };

    // Check if a parent menu contains the current path
    const hasSelectedChild = (children: MenuItemLink[]) => {
        return children.some(child => isSelected(child.path));
    };

    const renderMenuItem = (item: MenuItem) => {
        if (item.type === 'link') {
            return (
                <ListItem key={item.text} disablePadding>
                    <ListItemButton
                        component={RouterLink}
                        to={item.path}
                        selected={isSelected(item.path)}
                        sx={{
                            minHeight: 48,
                            justifyContent: drawerOpen ? 'initial' : 'center',
                            px: 2.5,
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 0,
                                mr: drawerOpen ? 3 : 'auto',
                                justifyContent: 'center',
                            }}
                        >
                            {item.icon}
                        </ListItemIcon>
                        <ListItemText
                            primary={item.text}
                            sx={{ opacity: drawerOpen ? 1 : 0 }}
                        />
                    </ListItemButton>
                </ListItem>
            );
        } else {
            const isOpen = openMenus[item.text] || hasSelectedChild(item.children);
            
            return (
                <React.Fragment key={item.text}>
                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={() => handleToggleMenu(item.text)}
                            sx={{
                                minHeight: 48,
                                justifyContent: drawerOpen ? 'initial' : 'center',
                                px: 2.5,
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: drawerOpen ? 3 : 'auto',
                                    justifyContent: 'center',
                                }}
                            >
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.text}
                                sx={{ opacity: drawerOpen ? 1 : 0 }}
                            />
                            {drawerOpen && (isOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItemButton>
                    </ListItem>
                    <Collapse in={drawerOpen && isOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {item.children.map(child => (
                                <ListItem key={child.text} disablePadding>
                                    <ListItemButton
                                        component={RouterLink}
                                        to={child.path}
                                        selected={isSelected(child.path)}
                                        sx={{
                                            minHeight: 48,
                                            justifyContent: drawerOpen ? 'initial' : 'center',
                                            pl: drawerOpen ? 4 : 2.5,
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 0,
                                                mr: drawerOpen ? 3 : 'auto',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {child.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={child.text}
                                            sx={{ opacity: drawerOpen ? 1 : 0 }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Collapse>
                </React.Fragment>
            );
        }
    };

    return (
        <>
            {/* Empty toolbar to account for app bar height */}
            <Box sx={{ height: theme => theme.mixins.toolbar.minHeight }} />
            
            {/* Toggle button */}
            <Box sx={{ 
                py: 1.5,
                display: 'flex', 
                justifyContent: drawerOpen ? 'flex-end' : 'center',
                px: 2,
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
            }}>
                <IconButton 
                    onClick={toggleDrawer} 
                    size="small"
                    sx={{ 
                        color: 'primary.main',
                    }}
                    aria-label={drawerOpen ? "Collapse sidebar" : "Expand sidebar"}
                >
                    {drawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </IconButton>
            </Box>
            
            {/* Navigation menu */}
            <Box sx={{ 
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100vh - 64px - 56px)' // Subtract toolbar and toggle button heights
            }}>
                {/* Top menu items */}
                <List>
                    {topMenuItems.map(item => renderMenuItem(item))}
                </List>

                {/* Middle menu items (Applications) */}
                <Box sx={{ flexGrow: 1 }}>
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <List>
                            {middleMenuItems.map(item => renderMenuItem(item))}
                        </List>
                    )}
                </Box>

                {/* Bottom menu items */}
                <Box sx={{ 
                    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                    mt: 'auto'
                }}>
                    <List>
                        {bottomMenuItems.map(item => renderMenuItem(item))}
                    </List>
                </Box>
            </Box>
        </>
    );
}